'use client'

import type { ReactNode } from 'react'
import { AnalyticsSection, chipClass } from './AnalyticsSection'
import { flowPayoutShare, hasFlowAmounts } from './MarketSummarySection'
import { PayoutLedger } from './PayoutLedger'
import { useMarketMoneyPayouts } from './market-money-data'
import {
  displayedCommissionRubles,
  formatDayMonth,
  formatPeriod,
  formatRangeWithYear,
  formatRubles,
  formatShare,
  payoutShareBasisPoints,
  pluralOrders,
  pluralRu,
  SALES_PAYOUT_DIFFERENCE,
  type MarketMoneyLedgerRow,
  type MarketMoneyPayouts,
  type MarketMoneyReport,
} from '@/lib/market-money'

const FLOW_REASONS: Record<string, string> = {
  no_payouts_in_range: 'Маркет ещё не выплатил деньги за доставки этого периода.',
}

function FlowCard({ report, ledger, busy }: { report: MarketMoneyReport; ledger: readonly MarketMoneyLedgerRow[] | null; busy: boolean }) {
  const { flow } = report
  const available = hasFlowAmounts(flow)
  const deliveryFrom = flow.deliveryFrom
  const deliveryTo = flow.deliveryTo
  const coveredRows = ledger && deliveryFrom !== null && deliveryTo !== null
    ? ledger.filter((row) => row.state === 'received' && row.deliveryFrom >= deliveryFrom && row.deliveryTo <= deliveryTo)
    : []
  const coverageMatches = coveredRows.length > 0 && coveredRows.length === flow.payoutCount
  const payoutDates = coveredRows.map((row) => row.payoutDate).sort()
  const payoutShare = flowPayoutShare(flow)
  const commissionShare = flow.commissionShareBasisPoints ?? (payoutShare === null ? null : 10_000 - payoutShare)
  const barPayout = Math.min(100, Math.max(0, (payoutShare ?? 0) / 100))
  const payoutFrom = flow.payoutFrom ?? (coverageMatches ? payoutDates[0] : null)
  const payoutTo = flow.payoutTo ?? (coverageMatches ? payoutDates[payoutDates.length - 1] : null)
  const flowLabel = [
    deliveryFrom !== null && deliveryTo !== null ? `Доставки ${formatDayMonth(deliveryFrom)}–${formatDayMonth(deliveryTo)}` : null,
    payoutFrom && payoutTo ? `выплаты с ${formatDayMonth(payoutFrom)} по ${formatDayMonth(payoutTo)}` : null,
  ].filter((part): part is string => part !== null).join(' · ')
  return (
    <article
      aria-label="Продажи и выплаты за период"
      aria-busy={busy}
      data-availability={available ? flow.availability : 'unavailable'}
      className={`surface-card flex flex-col gap-[18px] px-5 py-5 sm:px-6 sm:py-[22px] lg:col-span-8 ${busy ? 'opacity-60' : ''}`}
    >
      <div data-testid="flow-label" className="eyebrow tab-nums">
        {flowLabel || `Период ${formatRangeWithYear(report.range.from, report.range.to)}`}
      </div>
      {available && flow.salesKopecks !== null && flow.payoutKopecks !== null ? (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-0">
            <div className="flex flex-col gap-1.5 sm:border-r sm:border-rule sm:pr-[18px]">
              <span className="text-[12px] text-ink-muted">Продажи · доставлено</span>
              <span className="tab-nums whitespace-nowrap text-[24px] font-semibold sm:text-[28px]">{formatRubles(flow.salesKopecks)}</span>
              {coverageMatches && <span className="text-[12px] text-ink-faint">{pluralOrders(coveredRows.reduce((sum, row) => sum + row.orders, 0))}</span>}
            </div>
            <div className="flex flex-col gap-1.5 sm:border-r sm:border-rule sm:px-[18px]">
              <span className="text-[12px] text-ink-muted">Выплаты Маркета</span>
              <span className="tab-nums whitespace-nowrap text-[24px] font-semibold text-received sm:text-[28px]">{formatRubles(flow.payoutKopecks)}</span>
              <span className="text-[12px] text-ink-faint">{pluralRu(flow.payoutCount, ['выплата', 'выплаты', 'выплат'])}{payoutShare !== null ? ` · ${formatShare(payoutShare)}` : ''}</span>
            </div>
            <div className="flex flex-col gap-1.5 sm:pl-[18px]">
              <span className="text-[12px] text-ink-muted">{SALES_PAYOUT_DIFFERENCE.label}</span>
              <span className="tab-nums whitespace-nowrap text-[24px] font-semibold text-st-unpaid sm:text-[28px]">{flow.commissionKopecks === null ? '—' : `${displayedCommissionRubles(flow.salesKopecks, flow.payoutKopecks).toLocaleString('ru-RU')} ₽`}</span>
              {SALES_PAYOUT_DIFFERENCE.caption && <span data-testid="flow-commission-caption" className="text-[11px] leading-snug text-ink-faint">{SALES_PAYOUT_DIFFERENCE.caption}</span>}
              {commissionShare !== null && <span className="text-[12px] text-ink-faint">{formatShare(commissionShare)} от продаж</span>}
            </div>
          </div>
          {payoutShare !== null && commissionShare !== null && (
            <div className="flex flex-col gap-1.5">
              <div className="flex h-3.5 gap-0.5" aria-hidden="true">
                <div className="bg-st-delivered" style={{ width: `${barPayout}%` }} />
                <div className="bg-st-unpaid" style={{ width: `${100 - barPayout}%` }} />
              </div>
              <div className="flex justify-between text-[12px] text-ink-muted">
                <span>выплаты {formatShare(payoutShare)}</span>
                <span>{SALES_PAYOUT_DIFFERENCE.shortLabel} {formatShare(commissionShare)}</span>
              </div>
            </div>
          )}
          {flow.availability === 'partial' && <p className="text-[12px] text-gold-deep">Часть выплат за этот период ещё не пришла, суммы неполные.</p>}
        </>
      ) : (
        <div className="flex flex-col gap-1.5 py-4">
          <p className="text-[20px] font-semibold text-ink-muted">нет выплат за период</p>
          <p className="text-[12px] text-ink-muted">{(flow.reason && FLOW_REASONS[flow.reason]) || 'Данных о выплатах Маркета за этот период нет.'}</p>
        </div>
      )}
    </article>
  )
}

function UpcomingColumn({ payouts, busy }: { payouts: MarketMoneyPayouts; busy: boolean }) {
  const [next, ...rest] = payouts.upcoming
  const nextShare = next ? payoutShareBasisPoints(next.salesKopecks, next.payoutKopecks) : null
  return (
    <div aria-busy={busy} className={`flex flex-col gap-3 lg:col-span-4 ${busy ? 'opacity-60' : ''}`}>
      <article aria-label="Следующая выплата" className="flex flex-col gap-2.5 bg-onyx px-[22px] py-5 text-ivory">
        <div className="eyebrow !text-ink-faint">Следующая выплата</div>
        {next ? (
          <>
            <div className="flex items-baseline justify-between gap-3">
              <span className="tab-nums whitespace-nowrap text-[28px] font-semibold sm:text-[32px]">{formatRubles(next.payoutKopecks)}</span>
              <span className="tab-nums text-[18px] text-gold-soft">{formatDayMonth(next.payoutDate)}</span>
            </div>
            <div className="text-[12px] text-ink-faint">
              за доставки {formatPeriod(next.deliveryFrom, next.deliveryTo)} · {pluralOrders(next.orders)} · {next.state === 'week_open' ? 'неделя идёт' : nextShare !== null ? `${formatShare(nextShare, 0)} от продаж` : 'нет продаж'}
            </div>
          </>
        ) : <div className="text-[13px] text-ink-faint">Нет ожидаемых выплат</div>}
      </article>
      <article aria-label="Следующие выплаты" className="surface-card flex flex-col gap-2.5 px-[18px] py-4">
        <div className="eyebrow">Следующие выплаты</div>
        {rest.length === 0 && <div className="text-[13px] text-ink-faint">Других ожидаемых выплат нет</div>}
        {rest.map((row) => (
          <div key={row.payoutDate} className={`flex justify-between gap-3 text-[13px] ${row.state === 'week_open' ? 'text-ink-muted' : ''}`}>
            <span className="tab-nums">{formatDayMonth(row.payoutDate)} · доставки {formatPeriod(row.deliveryFrom, row.deliveryTo)}</span>
            <span className={`tab-nums text-right ${row.state === 'week_open' ? '' : 'font-semibold'}`}>{formatRubles(row.payoutKopecks)}{row.state === 'week_open' ? ' · неделя идёт' : ''}</span>
          </div>
        ))}
        <div className="flex justify-between gap-3 border-t border-rule pt-2.5 text-[13px] text-ink-muted">
          <span>Ещё не доставлено</span>
          <span className="tab-nums text-right">{pluralOrders(payouts.notDelivered.orders)} · {formatRubles(payouts.notDelivered.amountKopecks)}</span>
        </div>
      </article>
    </div>
  )
}

function LoadState({ loading, error, loadingText, emptyText, className }: { loading: boolean; error: string | null; loadingText: string; emptyText: string; className: string }) {
  if (error) return <div role="alert" className={`border border-st-unpaid/25 bg-st-unpaid/8 px-4 py-3 text-[13px] text-st-unpaid ${className}`}>{error}</div>
  if (loading) return <div role="status" className={`surface-card flex min-h-[160px] items-center justify-center p-5 text-[13px] text-ink-faint ${className}`}>{loadingText}</div>
  return <div className={`surface-card p-5 text-[13px] text-ink-muted ${className}`}>{emptyText}</div>
}

export function MarketPayoutsSection({ report, loading, onRefreshPayouts, refreshing, error = null, timeline, children }: {
  report: MarketMoneyReport | null
  loading: boolean
  onRefreshPayouts: () => void
  refreshing: boolean
  error?: string | null
  timeline?: ReactNode
  children?: ReactNode
}) {
  const payoutsState = useMarketMoneyPayouts()
  const { payouts } = payoutsState
  return (
    <AnalyticsSection
      id="payouts-section"
      index="02"
      eyebrow="Выплаты Маркета"
      title="Продажи и выплаты"
      aside={<>
        <span>Маркет платит через 2 недели после недели доставки</span>
        <button type="button" onClick={onRefreshPayouts} disabled={refreshing} className={`${chipClass(false)} disabled:cursor-wait disabled:opacity-60`}>
          <span className={`inline-block ${refreshing ? 'animate-spin' : ''}`} aria-hidden="true">↻</span> {refreshing ? 'Проверяем…' : 'Обновить выплаты'}
        </button>
      </>}
    >
      <div className="flex flex-col gap-3">
        <p data-testid="payouts-basis-explanation" className="max-w-[920px] text-[13px] leading-[1.55] text-ink-muted">
          <span className="font-semibold text-ink-2">В этой секции продажи считаются по дате доставки, а не по дате заказа.</span>{' '}
          Маркет платит за доставленные заказы через 2 недели после недели доставки, а сама доставка занимает ещё несколько дней.
          Поэтому суммы здесь не совпадают с «Продажами» в сводке: заказ, оформленный в конце июля и доставленный в августе,
          входит в июльские продажи и в августовские выплаты.
        </p>
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-12">
          {report
            ? <FlowCard report={report} ledger={payouts?.ledger ?? null} busy={loading} />
            : <LoadState className="lg:col-span-8" loading={loading} error={error} loadingText="Загружаем продажи и выплаты за период…" emptyText="Нет данных о продажах Маркета за этот период." />}
          {payouts
            ? <UpcomingColumn payouts={payouts} busy={payoutsState.loading} />
            : <LoadState className="lg:col-span-4" loading={payoutsState.loading} error={payoutsState.error} loadingText="Загружаем ближайшие выплаты…" emptyText="Нет данных о выплатах Маркета." />}
        </div>
        {timeline}
        {payouts && <PayoutLedger rows={payouts.ledger} highlightPayoutDate={payouts.upcoming[0]?.payoutDate ?? null} />}
      </div>
      {children}
    </AnalyticsSection>
  )
}
